import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { usePageSEO } from "@/hooks/usePageSEO";
import { FOUNDER, PERSON_JSONLD, ORGANIZATION_JSONLD } from "@/data/founderEntity";
import {
  ArrowLeft, ExternalLink, Award, Building2, Brain, Target,
  Globe, Mail, Phone, MapPin, Briefcase, CheckCircle, Mic,
  Zap, Users, Shield
} from "lucide-react";

export const JamilahProfile = () => {
  usePageSEO(
    "Jamilah N. Lawry | Business Strategist & Founder",
    "Meet Jamilah N. Lawry, professionally known as Nina Capone, founder of My Biz Consulting LLC and a strategist working across business, technology, media, and entrepreneurship."
  );

  const profilePageJsonLd = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    "@id": FOUNDER.profilePageId,
    "mainEntity": {
      ...PERSON_JSONLD,
      "@context": undefined,
    },
    "name": `${FOUNDER.primaryName} - Official Profile`,
    "url": `${FOUNDER.domain}${FOUNDER.profileUrl}`,
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": FOUNDER.domain },
        { "@type": "ListItem", "position": 2, "name": FOUNDER.primaryName, "item": `${FOUNDER.domain}${FOUNDER.profileUrl}` }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="jamilah-profile-page">
      {/* JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(profilePageJsonLd) }} />

      {/* Header */}
      <header className="bg-[#333333] text-white">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/mybiz-logo.png" alt="My Biz Consulting" className="h-7 brightness-0 invert" width="auto" height="28" />
          </Link>
          <Link to="/" className="text-neutral-300 hover:text-white text-sm flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
        </div>
      </header>

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="max-w-5xl mx-auto px-4 py-3">
        <ol className="flex items-center gap-2 text-xs text-neutral-500">
          <li><Link to="/" className="hover:text-neutral-900">Home</Link></li>
          <li>/</li>
          <li className="text-neutral-900 font-medium" aria-current="page">{FOUNDER.primaryName}</li>
        </ol>
      </nav>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <section className="mb-12">
          <div className="flex flex-col md:flex-row gap-8 items-start">
            <div className="flex-1">
              <h1 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-3">{FOUNDER.primaryName}</h1>
              <p className="text-lg text-[#2BC40E] font-medium mb-4">{FOUNDER.identityStatement}</p>
              <p className="text-neutral-700 leading-relaxed mb-6">{FOUNDER.bio}</p>

              <div className="flex flex-wrap gap-2 mb-6">
                {FOUNDER.jobTitles.map(t => (
                  <Badge key={t} variant="outline" className="border-neutral-300 text-neutral-700 py-1 px-3">{t}</Badge>
                ))}
              </div>

              <div className="flex flex-wrap gap-3">
                <Link to="/capability-statement">
                  <Button variant="outline" size="sm" className="border-neutral-300">
                    <Building2 className="w-3.5 h-3.5 mr-1.5" /> Capability Statement
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button variant="outline" size="sm" className="border-neutral-300">
                    <Briefcase className="w-3.5 h-3.5 mr-1.5" /> Services & Pricing
                  </Button>
                </Link>
                <Link to="/press">
                  <Button variant="outline" size="sm" className="border-neutral-300">
                    <Mic className="w-3.5 h-3.5 mr-1.5" /> Press & Media
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Current Professional Roles */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-neutral-900 mb-4 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-[#2BC40E]" /> Current Professional Roles
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <Card className="border-neutral-200">
              <CardContent className="p-5">
                <h3 className="font-semibold text-neutral-900 mb-1">Founder & Managing Consultant</h3>
                <p className="text-sm text-[#2BC40E] mb-2">{FOUNDER.organization}</p>
                <p className="text-sm text-neutral-600">Leading a technology-forward consulting firm specializing in systems integration, AI-driven strategy, and operational excellence for entrepreneurs, entertainers, and influencers.</p>
              </CardContent>
            </Card>
            <Card className="border-neutral-200">
              <CardContent className="p-5">
                <h3 className="font-semibold text-neutral-900 mb-1">Media Entrepreneur & Entertainer</h3>
                <p className="text-sm text-[#2BC40E] mb-2">as Nina Capone</p>
                <p className="text-sm text-neutral-600">Active in digital media, entertainment, and creative industries. Host of the 10 Minutes of Talk podcast. Connected to In Da Streets Radio and Inspire Da Streets Inc.</p>
              </CardContent>
            </Card>
            <Card className="border-neutral-200">
              <CardContent className="p-5">
                <h3 className="font-semibold text-neutral-900 mb-1">Author & Educator</h3>
                <p className="text-sm text-[#2BC40E] mb-2">Published Business Workbooks</p>
                <p className="text-sm text-neutral-600">Author of business strategy workbooks and educational resources designed to help entrepreneurs, creators, and business owners build structured, scalable operations.</p>
                <a href="https://app.thebookpatch.com/BookStoreResults?search=Jamilah%20N.%20Lawry&ddl=any" target="_blank" rel="noopener noreferrer" className="text-sm text-[#2BC40E] hover:underline flex items-center gap-1 mt-2">
                  View Published Works <ExternalLink className="w-3 h-3" />
                </a>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Areas of Expertise */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-neutral-900 mb-4 flex items-center gap-2">
            <Brain className="w-5 h-5 text-[#2BC40E]" /> Areas of Expertise
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {FOUNDER.expertise.map(e => (
              <div key={e} className="flex items-center gap-2 p-3 bg-neutral-50 rounded-lg border border-neutral-100">
                <CheckCircle className="w-4 h-4 text-[#39FF14] flex-shrink-0" />
                <span className="text-sm text-neutral-700">{e}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Organizations and Ventures */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-neutral-900 mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#2BC40E]" /> Organizations & Ventures
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { name: "My Biz Consulting LLC", role: "Founder & Managing Consultant", url: "/", desc: "AI-powered management consulting" },
              { name: "In Da Streets Radio", role: "Connected Venture", url: "https://indastreetsradio.com/", desc: "Internet radio & digital media platform", ext: true },
              { name: "Inspire Da Streets Inc.", role: "Connected Venture", url: "https://www.inspiredastreets.org/", desc: "Nonprofit — mental health, music & creative communities", ext: true },
            ].map(org => (
              <a key={org.name} href={org.ext ? org.url : undefined} onClick={org.ext ? undefined : (e) => { e.preventDefault(); window.location.href = org.url; }}
                target={org.ext ? "_blank" : undefined} rel={org.ext ? "noopener noreferrer" : undefined}
                className="p-4 bg-neutral-50 rounded-xl border border-neutral-200 hover:border-[#39FF14]/50 transition-all group block">
                <h3 className="font-semibold text-sm text-neutral-900 group-hover:text-[#2BC40E] flex items-center gap-1">
                  {org.name} {org.ext && <ExternalLink className="w-3 h-3 opacity-40" />}
                </h3>
                <p className="text-xs text-[#2BC40E] mb-1">{org.role}</p>
                <p className="text-xs text-neutral-500">{org.desc}</p>
              </a>
            ))}
          </div>
        </section>

        {/* Awards & Recognition */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-neutral-900 mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-[#2BC40E]" /> Awards & Recognition
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {FOUNDER.awards.map(award => (
              <a key={award.name} href={award.url} target="_blank" rel="noopener noreferrer"
                className="p-4 bg-amber-50 rounded-xl border border-amber-200 hover:shadow-md transition-all group flex items-center gap-3">
                <Award className="w-8 h-8 text-amber-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-sm text-neutral-900 group-hover:text-amber-700 flex items-center gap-1">{award.name} <ExternalLink className="w-3 h-3 opacity-40" /></p>
                </div>
              </a>
            ))}
          </div>
        </section>

        {/* Certifications */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-neutral-900 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#2BC40E]" /> Certifications & Affiliations
          </h2>
          <div className="flex flex-wrap gap-2">
            {FOUNDER.certifications.map(c => (
              <Badge key={c} variant="outline" className="border-neutral-300 text-neutral-700 py-1.5 px-3">{c}</Badge>
            ))}
          </div>
        </section>

        {/* Official Profiles */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-neutral-900 mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#2BC40E]" /> Official Profiles
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {[
              { label: "My Biz Consulting", url: FOUNDER.websites.business },
              { label: "Nina Capone Official", url: FOUNDER.websites.entertainment },
              ...Object.entries(FOUNDER.socialProfiles).map(([k, v]) => ({ label: k.charAt(0).toUpperCase() + k.slice(1), url: v }))
            ].map(p => (
              <a key={p.url} href={p.url} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 p-2.5 bg-neutral-50 rounded-lg hover:bg-neutral-100 transition-colors text-sm text-neutral-700 hover:text-[#2BC40E]">
                <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" /> {p.label}
              </a>
            ))}
          </div>
        </section>

        {/* Contact / Work With Jamilah */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-neutral-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-[#2BC40E]" /> Work With {FOUNDER.alternateName}
          </h2>
          <Card className="border-[#39FF14]/20 bg-gradient-to-r from-[#39FF14]/5 to-emerald-50">
            <CardContent className="p-6">
              <p className="text-neutral-700 mb-4">Interested in working with {FOUNDER.primaryName}? Start with a Business Clarity Assessment or book a strategy consultation.</p>
              <div className="flex flex-wrap gap-3 mb-6">
                <Link to="/clarity-assessment">
                  <Button className="bg-[#333333] hover:bg-[#444444] text-white">Start Free Assessment</Button>
                </Link>
                <Link to="/pricing">
                  <Button variant="outline" className="border-neutral-300">View Pricing</Button>
                </Link>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 text-sm text-neutral-600">
                <a href={`mailto:${FOUNDER.contact.email}`} className="flex items-center gap-2 hover:text-[#2BC40E]"><Mail className="w-4 h-4" /> {FOUNDER.contact.email}</a>
                <a href={`tel:${FOUNDER.contact.phone}`} className="flex items-center gap-2 hover:text-[#2BC40E]"><Phone className="w-4 h-4" /> {FOUNDER.contact.phone}</a>
                <span className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Folcroft, PA 19032</span>
              </div>
            </CardContent>
          </Card>
        </section>
      </main>
    </div>
  );
};

export default JamilahProfile;
